import React from 'react';
import {
  Button,
  Col,
  DatePicker,
  Icon,
  Input,
  Row,
  Select,
} from '@oscar/theme';
import { ActionField, AsyncField, Field, useFormContext } from '@oscar/form';
import { AddedFormProps } from 'common/components/FormList/types';
import { FORMAT_DATE } from 'common/constants';
import { APIS } from 'common/constants/endpoints';
import { SAP_STATUS_LIST } from 'common/constants/insulation';
import { debounce, set } from 'lodash';
import PriorityModal from './components/PriorityInput';
import { TextField, ReadOnlyInput } from 'common/components';
import '../style.scss';
import moment from 'moment';
import { getDateCorrect } from 'features/insulation/utils';

const { TextArea } = Input;
const { Option } = Select;
interface Props {
  handleActiveCreateForm: (value: boolean) => void;
  [name: string]: any;
}

const Text = ({ value }: { value?: string }) => {
  return <div>{value ? value : '<Auto-generated>'}</div>;
};

const CreateForm = (props: AddedFormProps) => {
  const { field, addItem, removeItem } = props;
  const [formContext] = useFormContext();
  if (!field) {
    return <div>Empty</div>;
  }

  const handleChangeFunctional = (value: any, options: any) => {
    const selectedItem = options.find((option: any) => option?.id === value);
    const form = formContext.form?.getFieldsValue();
    formContext?.form?.setFieldsValue(
      set(
        form,
        `listWorkDetail[${field!.key}].saP_LocationDescription`,
        selectedItem?.description,
      ),
    );
  };

  return (
    <div key={field!.key}>
      <div className="notification-detail">
        <div className="notification-detail-title">Search Result</div>
        <div className="notification-detail-content">
          <Row>
            <Col span={24}>
              <div className="search-header">
                <div className="search-header_id">
                  <div className="label-bold ">Notification ID</div>
                  <Field
                    name={[field!.name, 'saP_NotificationId']}
                    fieldKey={[field!.fieldKey, 'saP_NotificationId']}
                  >
                    <Text />
                  </Field>
                </div>
                <ActionField name="subForm" request={{ name: 'test' }}>
                  {(subProps: any) => {
                    const listWorkDetail = subProps.field.getFieldValue([
                      'listWorkDetail',
                    ]);
                    const itemAdd = listWorkDetail[listWorkDetail.length - 1];
                    const isEmpty =
                      itemAdd.saP_JobDescription &&
                      itemAdd.saP_FunctionalLocation &&
                      itemAdd.saP_Priority;
                    return (
                      <div>
                        <Button
                          size="small"
                          className="reset-button"
                          onClick={() => {
                            removeItem?.(field);
                          }}
                        >
                          Reset
                        </Button>
                        <Button
                          size="small"
                          type="primary"
                          disabled={!isEmpty}
                          onClick={() => {
                            addItem?.(field);
                          }}
                        >
                          Create
                        </Button>
                      </div>
                    );
                  }}
                </ActionField>
              </div>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Field
                {...field}
                name={[field!.name, 'saP_Status']}
                fieldKey={[field!.fieldKey, 'saP_Status']}
              >
                <TextField
                  label="Status"
                  mappingData={SAP_STATUS_LIST}
                  primary
                />
              </Field>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <p className="label-normal">Job Description</p>
              <Field
                {...field}
                name={[field!.name, 'saP_JobDescription']}
                fieldKey={[field!.fieldKey, 'saP_JobDescription']}
              >
                <TextArea
                  className="form-job-description"
                  placeholder="Lorem ipsum dolor sit amet..."
                  autoSize={{ minRows: 2, maxRows: 2 }}
                  maxLength={40}
                />
              </Field>
            </Col>
          </Row>
          <AsyncField
            name={'fetchLocation'}
            requests={[
              {
                name: 'location',
                url: APIS.SEARCH_LOCATION,
                method: 'GET',
                nextParams: (nextProps: any) => {
                  return {
                    limit: 6,
                    functionalLocation: nextProps?.customData?.textSearch || '',
                  };
                },
              },
            ]}
          >
            {(locationProps: any) => {
              const options = locationProps?.data?.location || [];
              const textSearch =
                locationProps?.requests?.[0]?.params?.functionalLocation || '';
              return (
                <Row gutter={15}>
                  <Col span={12}>
                    <p className="label-normal">Functional Location</p>

                    <Field
                      {...field}
                      name={[field!.fieldKey, 'saP_FunctionalLocation']}
                      fieldKey={[field!.fieldKey, 'saP_FunctionalLocation']}
                    >
                      <Select
                        value={textSearch}
                        showSearch
                        suffixIcon={<Icon icon="search" size={18} />}
                        className="select-search"
                        placeholder="Search Functional Location"
                        allowClear
                        onChange={(value: any) =>
                          handleChangeFunctional(value, options)
                        }
                        filterOption={false}
                        onSearch={debounce(value => {
                          locationProps.reloadData({ textSearch: value });
                        }, 300)}
                        notFoundContent={
                          <div>No Functional Location found</div>
                        }
                      >
                        {options.map?.((item: any) => {
                          return (
                            <Option value={item.id} key={item.id}>
                              {item.functionalLocation}
                            </Option>
                          );
                        })}
                      </Select>
                    </Field>
                  </Col>
                  <Col span={12}>
                    <p className="label-normal">Location Description</p>
                    <Field
                      name={[field!.name, 'saP_LocationDescription']}
                      fieldKey={[field!.fieldKey, 'saP_LocationDescription']}
                    >
                      <Input placeholder="" disabled />
                    </Field>
                  </Col>
                </Row>
              );
            }}
          </AsyncField>

          <Row gutter={15}>
            <Col className="gutter-row" span={12}>
              <p className="label-normal">Priority</p>
              <Field name={[field!.name]} fieldKey={[field!.fieldKey]}>
                <PriorityModal
                  mapData={{
                    sapConsequenceClassId: 'saP_ConsequenceClassId',
                    sapConsequenceCategoryId: 'saP_ConsequenceCategoryId',
                    sapProbabilityId: 'saP_ProbabilityClassId',
                    sapPriority: 'saP_Priority',
                  }}
                />
              </Field>
            </Col>
            <Col className="gutter-row" span={12}>
              <p className="label-normal">Maintenance Order</p>
              <Field
                name={[field!.name, 'saP_MaintenanceOrder']}
                fieldKey={[field!.fieldKey, 'saP_MaintenanceOrder']}
              >
                <Input placeholder="" disabled />
              </Field>
            </Col>
          </Row>
          <Row gutter={15}>
            <Col span={12}>
              <p className="label-normal">Start Date</p>
              <Field
                name={[field!.name, 'saP_StartDate']}
                fieldKey={[field!.fieldKey, 'saP_StartDate']}
              >
                <DatePicker
                  className="date-picker"
                  format={FORMAT_DATE.SHORT_MONTH_DATE}
                  inputReadOnly
                  disabled
                />
              </Field>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default CreateForm;
